import { Link, useNavigate, useParams } from 'react-router-dom';
import { Screen } from '../components/AppShell';
import { RegPlate, StatusText, StatusPill, Icon, Skeleton, EmptyState, Badge, SectionHeader } from '../components/ui';
import { api, type Asset, type Defect, type InspectionSummary, type Job } from '../lib/api';
import { useLoad, useAction } from '../lib/hooks';
import { assetDueList, formatDate, formatIsoWeek } from '../../../shared/schedule';
import './screens.css';

interface Payload {
  asset: Asset;
  inspections: InspectionSummary[];
  defects: Defect[];
  jobs: Job[];
  plan: string[];
  warning: string | null;
}

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { busy, error: actionError, run } = useAction();
  const { data, loading, error, reload } = useLoad<Payload>(() => api.get(`/assets/${id}`), [id]);

  const startInspection = () =>
    run(async () => {
      try {
        const result = await api.post<{ inspection: { id: string } }>('/inspections', { assetId: id });
        navigate(`/inspections/${result.inspection.id}/run`);
      } catch (err: any) {
        if (err?.status === 409 && err?.data?.existingId) {
          navigate(`/inspections/${err.data.existingId}/run`);
          return;
        }
        throw err;
      }
    });

  if (loading && !data) {
    return (
      <Screen
      onRefresh={reload} title="Vehicle" back="/fleet">
        <Skeleton variant="detail" rows={3} />
      </Screen>
    );
  }

  if (error || !data) {
    return (
      <Screen title="Vehicle" back="/fleet">
        <div className="notice notice-danger">{error ?? 'Not found.'}</div>
      </Screen>
    );
  }

  const { asset, inspections, defects, jobs, plan, warning } = data;
  const openDefects = defects.filter((d) => d.status === 'open');
  const dueList = assetDueList(asset).filter((e) => e.status.state !== 'unknown');
  const name = [asset.make, asset.model].filter(Boolean).join(' ');

  return (
    <Screen
      title={asset.reg_no ?? asset.fleet_no ?? 'Vehicle'}
      subtitle={asset.customer_name}
      back="/fleet"
      action={
        <Link to={`/fleet/${asset.id}/edit`} className="btn btn-ghost btn-sm">
          Edit
        </Link>
      }
    >
      <div className="stack-6">
        <header className="detail-head">
          <div className="row spread">
            <RegPlate reg={asset.reg_no} fleetNo={asset.fleet_no} size="lg" />
            <Badge tone={asset.kind === 'trailer' ? 'neutral' : 'hazard'}>
              {asset.kind === 'trailer' ? 'VEH002' : 'VEH001'}
            </Badge>
          </div>
          <div>
            <h2>{name || (asset.kind === 'trailer' ? 'Trailer' : 'Vehicle')}</h2>
            <p className="small muted" style={{ marginTop: 4 }}>
              {asset.axles} axles
              {asset.fleet_no && asset.reg_no ? ` · Fleet ${asset.fleet_no}` : ''}
              {asset.odometer != null
                ? ` · ${asset.odometer.toLocaleString('en-GB')} ${asset.odometer_unit === 'hubo' ? 'hubo' : asset.odometer_unit}`
                : ''}
            </p>
          </div>

          <button className="btn btn-primary btn-lg btn-block" onClick={startInspection} disabled={busy}>
            <Icon name="clipboard" size={19} />
            {busy ? 'Opening…' : 'Start safety inspection'}
          </button>
        </header>

        {actionError ? <div className="notice notice-danger">{actionError}</div> : null}
        {warning ? <div className="notice notice-warn">{warning}</div> : null}

        {openDefects.length > 0 && (
          <div className="notice notice-danger">
            <Icon name="warning" size={18} />
            <div>
              <strong>
                {openDefects.length} open defect{openDefects.length === 1 ? '' : 's'}
              </strong>
              <div style={{ marginTop: 2 }}>
                {openDefects.some((d) => d.severity === 'safety')
                  ? 'Includes a safety item. This vehicle should not return to service until rectified.'
                  : 'Rectify before the vehicle returns to service.'}
              </div>
            </div>
          </div>
        )}

        {/* ---- Dates ---- */}
        <section className="stack">
          <SectionHeader title="Due dates" />
          <div className="panel datelist">
            {dueList.length === 0 ? (
              <div className="daterow">
                <span className="muted small">No dates recorded yet.</span>
              </div>
            ) : (
              dueList.map((entry) => (
                <div key={entry.key} className="daterow">
                  <div className="grow">
                    <div className="daterow-title">{entry.title}</div>
                    <div className="daterow-date">{formatDate(entry.status.due)}</div>
                  </div>
                  {entry.status.state === 'overdue' ? (
                    <StatusPill state="overdue" size="sm">
                      {entry.status.label}
                    </StatusPill>
                  ) : (
                    <StatusText state={entry.status.state}>{entry.status.label}</StatusText>
                  )}
                </div>
              ))
            )}
          </div>
        </section>

        {/* ---- Forward plan ---- */}
        <section className="stack">
          <SectionHeader title="Forward plan" action={<span className="dim">{asset.inspection_interval_weeks}-weekly</span>} />
          <div className="planstrip">
            {plan.map((date, index) => (
              <div key={date} className={`planslot${index === 0 ? ' is-next' : ''}`}>
                <span className="planslot-week">{formatIsoWeek(date).split('-')[1]}</span>
                <span className="planslot-date">{formatDate(date).replace(/ \d{4}$/, '')}</span>
              </div>
            ))}
          </div>
          <p className="hint">Projected from the last completed inspection — the schedule DVSA expect at audit.</p>
        </section>

        {/* ---- Inspection history ---- */}
        <section className="stack">
          <SectionHeader title="Inspection history" count={inspections.length} />
          {inspections.length === 0 ? (
            <EmptyState title="No inspections yet" body="The first safety inspection will appear here." />
          ) : (
            <div className="panel divide">
              {inspections.map((insp) => (
                <Link
                  key={insp.id}
                  to={insp.locked_at ? `/inspections/${insp.id}` : `/inspections/${insp.id}/run`}
                  className="listrow"
                >
                  <div className="grow">
                    <div className="listrow-title">
                      {insp.inspection_date ? formatDate(insp.inspection_date) : 'Not dated'}
                    </div>
                    <div className="listrow-sub">
                      <span className="mono">{insp.ref}</span>
                      {insp.iso_week ? (
                        <>
                          <span className="dot" />
                          <span>Week {insp.iso_week}</span>
                        </>
                      ) : null}
                      {insp.inspector_name ? (
                        <>
                          <span className="dot" />
                          <span>{insp.inspector_name}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  {insp.status === 'defects' ? (
                    <StatusPill state="defects" size="sm">
                      {statusLabel(insp)}
                    </StatusPill>
                  ) : (
                    <StatusText state={insp.status}>{statusLabel(insp)}</StatusText>
                  )}
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* ---- Defect history ---- */}
        {defects.length > 0 && (
          <section className="stack">
            <SectionHeader title="Defect history" count={defects.length} />
            <div className="panel divide">
              {defects.slice(0, 15).map((defect) => (
                <div key={defect.id} className="listrow" style={{ alignItems: 'flex-start' }}>
                  <div className="grow">
                    <div className="listrow-title">{defect.description}</div>
                    <div className="listrow-sub wrap">
                      {defect.im_no ? <Badge>IM {defect.im_no}</Badge> : null}
                      <Badge tone={defect.severity}>
                        {defect.severity === 'safety' ? 'Safety' : defect.severity === 'repair' ? 'Repair' : 'Monitor'}
                      </Badge>
                      <span className="dot" />
                      <span>{formatDate(defect.created_at.slice(0, 10))}</span>
                    </div>
                    {defect.action_taken ? (
                      <div className="small muted" style={{ marginTop: 6 }}>
                        {defect.action_taken}
                        {defect.done_by ? ` — ${defect.done_by}` : ''}
                      </div>
                    ) : null}
                  </div>
                  {defect.status === 'open' ? (
                    <StatusPill state="open" size="sm">
                      Open
                    </StatusPill>
                  ) : (
                    <StatusText state="ok">{defect.status === 'deferred' ? 'Deferred' : 'Rectified'}</StatusText>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ---- Workshop jobs ---- */}
        <section className="stack">
          <SectionHeader
            title="Repairs & maintenance"
            action={
              <Link to={`/jobs/new?assetId=${asset.id}`}>
                Add job <Icon name="chevron" size={14} />
              </Link>
            }
          />
          {jobs.length === 0 ? (
            <EmptyState title="No jobs recorded" body="Repairs and servicing logged against this vehicle appear here." />
          ) : (
            <div className="panel divide">
              {jobs.map((job) => (
                <Link key={job.id} to={`/jobs/${job.id}`} className="listrow">
                  <div className="grow">
                    <div className="listrow-title truncate">{job.title}</div>
                    <div className="listrow-sub">
                      <span className="mono">{job.ref}</span>
                      <span className="dot" />
                      <span>{formatDate(job.opened_at)}</span>
                      <span className="dot" />
                      <span className="money">£{job.total.toFixed(2)}</span>
                    </div>
                  </div>
                  <StatusText state={job.status === 'complete' || job.status === 'invoiced' ? 'ok' : 'due'}>
                    {job.status === 'in_progress' ? 'In progress' : job.status[0].toUpperCase() + job.status.slice(1)}
                  </StatusText>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* ---- Reference ---- */}
        <section className="stack">
          <SectionHeader title="Details" />
          <dl className="kv">
            {asset.vin ? (
              <>
                <dt>VIN</dt>
                <dd className="mono">{asset.vin}</dd>
              </>
            ) : null}
            {asset.chassis_no ? (
              <>
                <dt>Chassis</dt>
                <dd className="mono">{asset.chassis_no}</dd>
              </>
            ) : null}
            {asset.body_type ? (
              <>
                <dt>Body</dt>
                <dd>{asset.body_type}</dd>
              </>
            ) : null}
            {asset.first_use_date ? (
              <>
                <dt>First used</dt>
                <dd>{formatDate(asset.first_use_date)}</dd>
              </>
            ) : null}
            <dt>Interval</dt>
            <dd>{asset.inspection_interval_weeks} weeks</dd>
            {asset.notes ? (
              <>
                <dt>Notes</dt>
                <dd>{asset.notes}</dd>
              </>
            ) : null}
          </dl>
        </section>
      </div>
    </Screen>
  );
}

function statusLabel(insp: InspectionSummary) {
  if (insp.status === 'amended') return 'Superseded';
  if (insp.status === 'signed') return insp.overall_result === 'satisfactory' ? 'Pass' : 'Rectified';
  if (insp.status === 'defects') return insp.signed_at ? 'Rectifying' : 'Defects';
  return 'Draft';
}
